import React, { useState } from 'react';
import { FileText, MapPin, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

export default function QuoteRequestModal({ material, selectedVariant, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const minimum = Number(material?.minimumOrder || material?.minOrder || 1);
  const [quantity, setQuantity] = useState(minimum > 0 ? minimum : 1);
  const [note, setNote] = useState('');
  const [address, setAddress] = useState({
    street: user?.address?.street || '', city: user?.address?.city || '',
    state: user?.address?.state || '', pincode: user?.address?.pincode || ''
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) { onClose(); navigate('/login'); return; }
    if (user.role !== 'customer') return toast.warning('Only customers can request quotes');
    if (!address.city.trim() || !address.state.trim() || !address.pincode.trim()) return toast.error('Enter city, state, and pincode');
    setSubmitting(true);
    try {
      await API.post('/quotes', {
        material: material._id, quantity: Number(quantity),
        unit: selectedVariant?.unit || material.unit, variant: selectedVariant?.size || '',
        deliveryAddress: address, customerNote: note
      });
      toast.success('Quote request sent to the supplier');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to send quote request');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="quote-modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="quote-modal" role="dialog" aria-modal="true" aria-labelledby="quote-title">
        <div className="quote-modal-head">
          <div><FileText size={18} /><div><span>Request supplier pricing</span><h2 id="quote-title">{material.name}</h2></div></div>
          <button onClick={onClose} aria-label="Close quote request"><X size={18} /></button>
        </div>
        <div className="quote-modal-body">
          <div className="quote-product-summary">
            <div><span>Supplier</span><strong>{material.owner?.shopName || 'Verified supplier'}</strong></div>
            <div><span>Product</span><strong>{selectedVariant?.size || material.brand || material.category}</strong></div>
          </div>
          <div className="quote-form-row">
            <label>Quantity<input className="form-input" type="number" min={minimum || 1} value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
            <label>Unit<input className="form-input" value={selectedVariant?.unit || material.unit || 'unit'} disabled /></label>
          </div>
          <div className="quote-address-title"><MapPin size={14} /> Delivery location</div>
          <input className="form-input" placeholder="Street / Area" value={address.street} onChange={e => setAddress(a => ({ ...a, street: e.target.value }))} />
          <div className="quote-form-row quote-address-row">
            <input className="form-input" placeholder="City" value={address.city} onChange={e => setAddress(a => ({ ...a, city: e.target.value }))} />
            <input className="form-input" placeholder="State" value={address.state} onChange={e => setAddress(a => ({ ...a, state: e.target.value }))} />
            <input className="form-input" placeholder="Pincode" value={address.pincode} onChange={e => setAddress(a => ({ ...a, pincode: e.target.value }))} />
          </div>
          <label className="quote-note">Requirements or delivery notes<textarea className="form-input" maxLength={500} value={note} onChange={e => setNote(e.target.value)} placeholder="Mention grade, size, preferred delivery date, or other requirements" /></label>
        </div>
        <div className="quote-modal-actions">
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={submitting}><Send size={15} /> {submitting ? 'Sending...' : 'Send Request'}</button>
        </div>
      </div>
    </div>
  );
}
